/**
 * Gallery Enrichment API
 *
 * Enriches gallery records with job data from Jobber via the existing
 * fetchJobLineItems() API. This reuses the existing infrastructure:
 *
 * Flow: Frontend → jobber-line-items edge function → N8N → Jobber GraphQL
 *
 * @see /src/features/jobber/api/jobDetails.ts for the underlying API
 * @see /apps/production/n8n-jobber-line-items for the worker
 */

import { refreshJobDetails } from '@/features/jobber/api/jobDetails'
import { supabase } from '@/lib/supabase'
import type { JobDetailsComplete } from '@/features/jobber/types'

/**
 * Maps gallery franchise_region values to the N8N API franchise parameter.
 * Most regions fall back to 'hamilton' except north_york which has its own endpoint.
 */
const FRANCHISE_TO_ENDPOINT: Record<string, 'hamilton' | 'northyork'> = {
  HQ: 'hamilton',
  hamilton: 'hamilton',
  north_york: 'northyork',
  northyork: 'northyork',
  london: 'hamilton',
  toronto: 'hamilton',
  mississauga: 'hamilton',
  brampton: 'hamilton',
  scarborough: 'northyork',
}

export interface EnrichmentResult {
  success: boolean
  galleryId: number
  jobNumber?: number
  enrichmentStatus: 'enriched' | 'failed'
  errorMessage?: string
}

/**
 * Enriches a gallery with job data from Jobber.
 *
 * @param galleryId - The gallery ID to enrich
 * @param jobId - The Jobber job ID (base64-encoded GraphQL ID like "Z2lkOi8vSm9iYmVyL0pvYi8xMjM=")
 * @param franchiseRegion - The franchise region (will be mapped to hamilton/northyork)
 * @returns EnrichmentResult with success status and any error details
 *
 * @example
 * ```ts
 * const result = await enrichGalleryFromJobber(123, 'Z2lkOi8vSm9iYmVyL0pvYi8xMjM=', 'hamilton')
 * if (result.success) {
 *   console.log(`Enriched gallery with job #${result.jobNumber}`)
 * }
 * ```
 */
export async function enrichGalleryFromJobber(
  galleryId: number,
  jobId: string,
  franchiseRegion: string
): Promise<EnrichmentResult> {
  // Map franchise region to API endpoint
  const franchise = FRANCHISE_TO_ENDPOINT[franchiseRegion.toLowerCase()] || 'hamilton'

  try {
    // 1. Fetch fresh job details from Jobber (bypass cache)
    const jobData: JobDetailsComplete = await refreshJobDetails(jobId, franchise)

    // 2. Transform and upsert to gallery_jobber_details
    const { error } = await supabase.from('gallery_jobber_details').upsert(
      {
        gallery_id: galleryId,
        job_id: jobId,
        franchise_region: franchiseRegion,
        // Client info
        client_name: `${jobData.client.firstName} ${jobData.client.lastName}`.trim(),
        client_email: jobData.client.emails[0] || null,
        client_phone: jobData.client.phones[0] || null,
        // Property/service location
        service_address: jobData.property.street1,
        service_city: jobData.property.city,
        service_province: jobData.property.province,
        service_postal_code: jobData.property.postalCode,
        // Line items and visits as JSON
        line_items: jobData.lineItems,
        visits: jobData.visits,
        // New enrichment columns
        job_number: jobData.job.jobNumber,
        job_status: jobData.job.jobStatus,
        job_total: jobData.job.totalAmount,
        jobber_web_uri: jobData.job.jobberWebUri || null,
        // Enrichment tracking
        enrichment_status: 'enriched',
        error_message: null,
        fetched_on: new Date().toISOString(),
      },
      { onConflict: 'gallery_id' }
    )

    if (error) {
      console.error('[enrichGalleryFromJobber] Supabase upsert error:', error)
      throw new Error(`Database error: ${error.message}`)
    }

    return {
      success: true,
      galleryId,
      jobNumber: jobData.job.jobNumber,
      enrichmentStatus: 'enriched',
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    console.error('[enrichGalleryFromJobber] Error:', errorMessage)

    // Try to record the failure in the database
    try {
      await supabase.from('gallery_jobber_details').upsert(
        {
          gallery_id: galleryId,
          job_id: jobId,
          franchise_region: franchiseRegion,
          enrichment_status: 'failed',
          error_message: errorMessage,
          fetched_on: new Date().toISOString(),
        },
        { onConflict: 'gallery_id' }
      )
    } catch (dbError) {
      console.error('[enrichGalleryFromJobber] Failed to record error in database:', dbError)
    }

    return {
      success: false,
      galleryId,
      enrichmentStatus: 'failed',
      errorMessage,
    }
  }
}
